import { useState, useRef, useEffect } from "react";
import {
  POKEMON_TYPES,
  getSuperEffective,
  getNotVeryEffective,
  getNoEffect,
  TYPE_CHART,
} from "@/data/types";
import { TypeBadge } from "./TypeBadge";
import { DualTypeBadge } from "./DualTypeBadge";
import logo from "../logo.svg";

export function TypeChart() {
  const [focusedType, setFocusedType] = useState<string | null>(null);
  const [sourceType, setSourceType] = useState<string | null>(null);

  // Initialize state from URL hash
  const [selectedType, setSelectedType] = useState<string | null>(() => {
    const hash = window.location.hash.slice(1);
    // Check if it's a dual type combination (contains +)
    if (hash && hash.includes("+")) {
      const types = hash.split("+");
      if (
        types.length === 2 &&
        POKEMON_TYPES.some((t) => t.name === types[0])
      ) {
        return types[0];
      }
    }
    return null;
  });

  const [focusedDualType, setFocusedDualType] = useState<string | null>(null);

  const typeRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const dualTypeRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const hasInitializedRef = useRef(false);

  // Handle browser back/forward buttons
  useEffect(() => {
    const handlePopState = (_event: PopStateEvent) => {
      const hash = window.location.hash.slice(1);

      if (hash && hash.includes("+")) {
        // Dual type combination
        const types = hash.split("+");
        if (
          types.length === 2 &&
          POKEMON_TYPES.some((t) => t.name === types[0])
        ) {
          setSelectedType(types[0]);
          // Focus the specific combination if not the same type
          if (types[0] !== types[1]) {
            setTimeout(() => {
              setFocusedDualType(`${types[0]}+${types[1]}`);
            }, 100);
          }
        }
      } else if (hash && POKEMON_TYPES.some((t) => t.name === hash)) {
        // Single type - go to main chart and focus
        setSelectedType(null);
        setTimeout(() => {
          setFocusedType(hash);
        }, 100);
      } else {
        // No hash or invalid - go to main chart
        setSelectedType(null);
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // Handle initial focus from URL on mount - only once
  useEffect(() => {
    if (!hasInitializedRef.current) {
      hasInitializedRef.current = true;
      const hash = window.location.hash.slice(1);

      if (hash && hash.includes("+")) {
        // Dual type combination - focus after mount
        const types = hash.split("+");
        if (
          types.length === 2 &&
          types[0] !== types[1] &&
          POKEMON_TYPES.some((t) => t.name === types[1])
        ) {
          queueMicrotask(() => setFocusedDualType(`${types[0]}+${types[1]}`));
        }
      } else if (
        // Single type hash should focus on main chart
        hash &&
        !hash.includes("+") &&
        POKEMON_TYPES.some((t) => t.name === hash)
      ) {
        queueMicrotask(() => setFocusedType(hash));
      }
    }
  }, []);

  useEffect(() => {
    if (focusedType && typeRefs.current[focusedType]) {
      typeRefs.current[focusedType]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      // Clear focus after animation
      const timer = setTimeout(() => {
        setFocusedType(null);
        setSourceType(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [focusedType]);

  useEffect(() => {
    if (focusedDualType && dualTypeRefs.current[focusedDualType]) {
      dualTypeRefs.current[focusedDualType]?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      // Clear focus after animation
      const timer = setTimeout(() => {
        setFocusedDualType(null);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [focusedDualType]);

  const handleTypeClick = (typeName: string, source: string) => {
    setSourceType(source);
    setFocusedType(typeName);
    // Push state for browser navigation between focused types
    window.history.pushState({ focusedType: typeName }, "", `#${typeName}`);
  };

  const handleCenterTypeClick = (typeName: string) => {
    setSelectedType(typeName);
    window.scrollTo({ top: 0, behavior: "smooth" });
    // Push new state to history with dual type URL format (Type+Type for single type view)
    window.history.pushState(
      { selectedType: typeName },
      "",
      `#${typeName}+${typeName}`
    );
  };

  const handleDualTypeClick = (type1: string, type2: string) => {
    // Navigate to the type that isn't currently selected
    const targetType = type1 === selectedType ? type2 : type1;
    // Go back to main view and focus the target type
    setSelectedType(null);
    // Push state to navigate to the other type on main chart
    window.history.pushState({ focusedType: targetType }, "", `#${targetType}`);
    // Use setTimeout to allow state to update before setting focus
    setTimeout(() => {
      setFocusedType(targetType);
    }, 100);
  };

  const handleDualViewTypeClick = (typeName: string) => {
    // Return to main chart and focus the clicked type
    setSelectedType(null);
    // Push state to navigate to that type on main chart
    window.history.pushState({ focusedType: typeName }, "", `#${typeName}`);
    setTimeout(() => {
      setFocusedType(typeName);
    }, 100);
  };

  const handleBackToMainChart = () => {
    setSelectedType(null);
    // Use back navigation
    window.history.back();
  };

  const handleRowClick = (typeName: string) => {
    // Update URL without scrolling
    window.history.replaceState({ focusedType: typeName }, "", `#${typeName}`);
  };

  const handleDualTypeRowClick = (type1: string, type2: string) => {
    // Update URL without scrolling for specific dual type combination
    window.history.replaceState(
      { selectedType: type1, combinedWith: type2 },
      "",
      `#${type1}+${type2}`
    );
  };

  const calculateDualTypeEffectiveness = (
    type1: string,
    type2: string,
    attacking: boolean
  ) => {
    if (attacking) {
      // For attacking, we need to find the best effectiveness against dual types
      const results: Record<string, number> = {};

      POKEMON_TYPES.forEach((defType) => {
        const effectiveness1 = TYPE_CHART[type1]?.[defType.name] ?? 1;
        const effectiveness2 = TYPE_CHART[type2]?.[defType.name] ?? 1;
        // Take the better of the two attacks
        results[defType.name] = Math.max(effectiveness1, effectiveness2);
      });

      return results;
    } else {
      // For defending, multiply the effectiveness of both types
      const results: Record<string, number> = {};

      POKEMON_TYPES.forEach((attType) => {
        const effectiveness1 = TYPE_CHART[attType.name]?.[type1] ?? 1;
        const effectiveness2 = TYPE_CHART[attType.name]?.[type2] ?? 1;
        results[attType.name] = effectiveness1 * effectiveness2;
      });

      return results;
    }
  };

  if (selectedType) {
    return (
      <div className="w-full mx-auto px-2 sm:px-4 py-4 sm:py-8">
        {/* Logo and Title */}
        <div className="flex items-center justify-center gap-4 mb-6 sm:mb-8">
          <img
            src={logo}
            alt="Pokemon Logo"
            className="h-12 w-12 sm:h-16 sm:w-16"
          />
          <h1 className="text-3xl sm:text-4xl font-bold">Pokémon Type Chart</h1>
        </div>

        <div className="max-w-[2000px] mx-auto">
          {/* Header with back button */}
          <div className="flex items-center justify-between mb-2">
            <button
              onClick={handleBackToMainChart}
              className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:opacity-80 transition-opacity"
            >
              ← Back to All Types
            </button>

            <h2 className="text-lg sm:text-2xl font-bold text-center flex-1">
              {selectedType} Type Combinations
            </h2>

            {/* Spacer to balance the layout */}
            <div className="w-[140px]" />
          </div>

          {/* Single type row first */}
          {(() => {
            const type = POKEMON_TYPES.find((t) => t.name === selectedType)!;
            const weakTo: string[] = [];
            const resistsTo: string[] = [];
            const immuneTo: string[] = [];

            POKEMON_TYPES.forEach((attackingType) => {
              const superEffective = getSuperEffective(attackingType.name);
              const notVeryEffective = getNotVeryEffective(attackingType.name);
              const noEffect = getNoEffect(attackingType.name);

              if (superEffective.includes(type.name)) {
                weakTo.push(attackingType.name);
              }
              if (notVeryEffective.includes(type.name)) {
                resistsTo.push(attackingType.name);
              }
              if (noEffect.includes(type.name)) {
                immuneTo.push(attackingType.name);
              }
            });

            const superEffective = getSuperEffective(type.name);
            const notVeryEffective = getNotVeryEffective(type.name);
            const noEffect = getNoEffect(type.name);

            return (
              <div
                ref={(el) =>
                  (dualTypeRefs.current[`${selectedType}+${selectedType}`] = el)
                }
                onClick={() =>
                  handleDualTypeRowClick(selectedType, selectedType)
                }
                className={`bg-card rounded-lg border p-4 mb-4 cursor-pointer hover:border-primary/50 transition-all duration-300 ${
                  focusedDualType === `${selectedType}+${selectedType}`
                    ? "border-primary border-2 shadow-lg scale-[1.02]"
                    : "border-border"
                }`}
              >
                <div className="grid grid-cols-[1fr_auto_auto_auto_1fr] gap-2 sm:gap-4 items-center">
                  {/* Left: Defending */}
                  <div className="flex flex-col gap-3">
                    {weakTo.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-muted-foreground mb-1.5 text-right">
                          WEAK TO (2×)
                        </div>
                        <div className="flex flex-wrap gap-2 justify-end">
                          {weakTo.map((t) => (
                            <TypeBadge
                              key={t}
                              typeName={t}
                              onClick={() => handleDualViewTypeClick(t)}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {resistsTo.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-muted-foreground mb-1.5 text-right">
                          RESISTS (0.5×)
                        </div>
                        <div className="flex flex-wrap gap-2 justify-end">
                          {resistsTo.map((t) => (
                            <TypeBadge
                              key={t}
                              typeName={t}
                              variant="text"
                              onClick={() => handleDualViewTypeClick(t)}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {immuneTo.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-muted-foreground mb-1.5 text-right">
                          IMMUNE TO (0×)
                        </div>
                        <div className="flex flex-wrap gap-2 justify-end">
                          {immuneTo.map((t) => (
                            <TypeBadge
                              key={t}
                              typeName={t}
                              variant="text"
                              strikethrough
                              onClick={() => handleDualViewTypeClick(t)}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="text-lg sm:text-2xl text-muted-foreground">
                    →
                  </div>

                  {/* Center */}
                  <div className="flex items-center justify-center">
                    <TypeBadge
                      typeName={type.name}
                      className="text-sm sm:text-base px-3 sm:px-5 py-2 sm:py-3 min-w-[80px] sm:min-w-[120px] font-bold"
                      onClick={() => handleDualViewTypeClick(type.name)}
                    />
                  </div>

                  <div className="text-lg sm:text-2xl text-muted-foreground">
                    →
                  </div>

                  {/* Right: Attacking */}
                  <div className="flex flex-col gap-3">
                    {superEffective.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-muted-foreground mb-1.5">
                          SUPER EFFECTIVE (2×)
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {superEffective.map((t) => (
                            <TypeBadge
                              key={t}
                              typeName={t}
                              onClick={() => handleDualViewTypeClick(t)}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {notVeryEffective.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-muted-foreground mb-1.5">
                          NOT VERY EFFECTIVE (0.5×)
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {notVeryEffective.map((t) => (
                            <TypeBadge
                              key={t}
                              typeName={t}
                              variant="text"
                              onClick={() => handleDualViewTypeClick(t)}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                    {noEffect.length > 0 && (
                      <div>
                        <div className="text-xs font-semibold text-muted-foreground mb-1.5">
                          NO EFFECT (0×)
                        </div>
                        <div className="flex flex-wrap gap-2">
                          {noEffect.map((t) => (
                            <TypeBadge
                              key={t}
                              typeName={t}
                              variant="text"
                              strikethrough
                              onClick={() => handleDualViewTypeClick(t)}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })()}

          {/* Dual type combinations */}
          <div className="space-y-3">
            {POKEMON_TYPES.filter((t) => t.name !== selectedType).map(
              (secondType) => {
                const defending = calculateDualTypeEffectiveness(
                  selectedType,
                  secondType.name,
                  false
                );
                const attacking = calculateDualTypeEffectiveness(
                  selectedType,
                  secondType.name,
                  true
                );

                const weak4x: string[] = [];
                const weak2x: string[] = [];
                const resist05x: string[] = [];
                const resist025x: string[] = [];
                const immune: string[] = [];

                Object.entries(defending).forEach(([type, eff]) => {
                  if (eff === 4) weak4x.push(type);
                  else if (eff === 2) weak2x.push(type);
                  else if (eff === 0.5) resist05x.push(type);
                  else if (eff === 0.25) resist025x.push(type);
                  else if (eff === 0) immune.push(type);
                });

                const superEff2x: string[] = [];
                const notVeryEff05x: string[] = [];
                const noEffectAtk: string[] = [];

                Object.entries(attacking).forEach(([type, eff]) => {
                  if (eff === 2) superEff2x.push(type);
                  else if (eff === 0.5) notVeryEff05x.push(type);
                  else if (eff === 0) noEffectAtk.push(type);
                });

                const dualTypeKey = `${selectedType}+${secondType.name}`;

                return (
                  <div
                    key={secondType.name}
                    ref={(el) => (dualTypeRefs.current[dualTypeKey] = el)}
                    onClick={() =>
                      handleDualTypeRowClick(selectedType, secondType.name)
                    }
                    className={`bg-card rounded-lg border p-2 sm:p-4 cursor-pointer hover:border-primary/50 transition-all duration-300 ${
                      focusedDualType === dualTypeKey
                        ? "border-primary border-2 shadow-lg scale-[1.02]"
                        : "border-border"
                    }`}
                  >
                    <div className="grid grid-cols-[1fr_auto_auto_auto_1fr] gap-1 sm:gap-3 items-center">
                      {/* Left: Defending */}
                      <div className="flex flex-col gap-2 sm:gap-3">
                        {weak4x.length > 0 && (
                          <div>
                            <div className="text-[10px] sm:text-xs font-semibold text-destructive mb-1 text-right">
                              WEAK TO (4×)
                            </div>
                            <div className="flex flex-wrap gap-1 sm:gap-2 justify-end">
                              {weak4x.map((t) => (
                                <TypeBadge
                                  key={t}
                                  typeName={t}
                                  className="ring-2 ring-destructive"
                                  onClick={() => handleDualViewTypeClick(t)}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                        {weak2x.length > 0 && (
                          <div>
                            <div className="text-[10px] sm:text-xs font-semibold text-muted-foreground mb-1 text-right">
                              WEAK TO (2×)
                            </div>
                            <div className="flex flex-wrap gap-1 sm:gap-2 justify-end">
                              {weak2x.map((t) => (
                                <TypeBadge
                                  key={t}
                                  typeName={t}
                                  onClick={() => handleDualViewTypeClick(t)}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                        {resist05x.length > 0 && (
                          <div>
                            <div className="text-[10px] sm:text-xs font-semibold text-muted-foreground mb-1 text-right">
                              RESISTS (0.5×)
                            </div>
                            <div className="flex flex-wrap gap-1 sm:gap-2 justify-end">
                              {resist05x.map((t) => (
                                <TypeBadge
                                  key={t}
                                  typeName={t}
                                  variant="text"
                                  onClick={() => handleDualViewTypeClick(t)}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                        {resist025x.length > 0 && (
                          <div>
                            <div className="text-[10px] sm:text-xs font-semibold text-green-600 dark:text-green-400 mb-1 text-right">
                              RESISTS (0.25×)
                            </div>
                            <div className="flex flex-wrap gap-1 sm:gap-2 justify-end">
                              {resist025x.map((t) => (
                                <TypeBadge
                                  key={t}
                                  typeName={t}
                                  variant="text"
                                  className="ring-2 ring-green-600 dark:ring-green-400"
                                  onClick={() => handleDualViewTypeClick(t)}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                        {immune.length > 0 && (
                          <div>
                            <div className="text-[10px] sm:text-xs font-semibold text-muted-foreground mb-1 text-right">
                              IMMUNE TO (0×)
                            </div>
                            <div className="flex flex-wrap gap-1 sm:gap-2 justify-end">
                              {immune.map((t) => (
                                <TypeBadge
                                  key={t}
                                  typeName={t}
                                  variant="text"
                                  strikethrough
                                  onClick={() => handleDualViewTypeClick(t)}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>

                      <div className="text-lg sm:text-2xl text-muted-foreground">
                        →
                      </div>

                      {/* Center: Dual Type Badge */}
                      <div className="flex items-center justify-center">
                        <DualTypeBadge
                          primaryType={selectedType}
                          secondaryType={secondType.name}
                          className="text-sm sm:text-base px-3 sm:px-5 py-2 sm:py-3 min-w-[80px] sm:min-w-[120px] font-bold"
                          onClick={() =>
                            handleDualTypeClick(selectedType, secondType.name)
                          }
                        />
                      </div>

                      <div className="text-lg sm:text-2xl text-muted-foreground">
                        →
                      </div>

                      {/* Right: Attacking */}
                      <div className="flex flex-col gap-2 sm:gap-3">
                        {superEff2x.length > 0 && (
                          <div>
                            <div className="text-[10px] sm:text-xs font-semibold text-muted-foreground mb-1">
                              SUPER EFFECTIVE (2×)
                            </div>
                            <div className="flex flex-wrap gap-1 sm:gap-2">
                              {superEff2x.map((t) => (
                                <TypeBadge
                                  key={t}
                                  typeName={t}
                                  onClick={() => handleDualViewTypeClick(t)}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                        {notVeryEff05x.length > 0 && (
                          <div>
                            <div className="text-[10px] sm:text-xs font-semibold text-muted-foreground mb-1">
                              NOT VERY EFFECTIVE (0.5×)
                            </div>
                            <div className="flex flex-wrap gap-1 sm:gap-2">
                              {notVeryEff05x.map((t) => (
                                <TypeBadge
                                  key={t}
                                  typeName={t}
                                  variant="text"
                                  onClick={() => handleDualViewTypeClick(t)}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                        {noEffectAtk.length > 0 && (
                          <div>
                            <div className="text-[10px] sm:text-xs font-semibold text-muted-foreground mb-1">
                              NO EFFECT (0×)
                            </div>
                            <div className="flex flex-wrap gap-1 sm:gap-2">
                              {noEffectAtk.map((t) => (
                                <TypeBadge
                                  key={t}
                                  typeName={t}
                                  variant="text"
                                  strikethrough
                                  onClick={() => handleDualViewTypeClick(t)}
                                />
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              }
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full mx-auto px-2 sm:px-4 py-4 sm:py-8">
      {/* Logo and Title */}
      <div className="flex items-center justify-center gap-4 mb-6 sm:mb-8">
        <img
          src={logo}
          alt="Pokemon Logo"
          className="h-12 w-12 sm:h-16 sm:w-16"
        />
        <h1 className="text-3xl sm:text-4xl font-bold">Pokémon Type Chart</h1>
      </div>

      <div className="max-w-[2000px] mx-auto">
        {/* Column Headers */}
        <div className="grid grid-cols-[1fr_auto_1fr] gap-2 sm:gap-4 mb-3 sm:mb-4 px-2 sm:px-4">
          <h2 className="text-lg sm:text-2xl font-bold text-right">
            Attacking
          </h2>
          <div className="w-16 sm:w-32" />
          <h2 className="text-lg sm:text-2xl font-bold text-left">Defending</h2>
        </div>

        {/* Type Rows */}
        <div className="space-y-2 sm:space-y-3">
          {POKEMON_TYPES.map((type) => {
            // Calculate defending (what this type is weak to, resists, immune to)
            const weakTo: string[] = [];
            const resistsTo: string[] = [];
            const immuneTo: string[] = [];

            POKEMON_TYPES.forEach((attackingType) => {
              const superEffective = getSuperEffective(attackingType.name);
              const notVeryEffective = getNotVeryEffective(attackingType.name);
              const noEffect = getNoEffect(attackingType.name);

              if (superEffective.includes(type.name)) {
                weakTo.push(attackingType.name);
              }
              if (notVeryEffective.includes(type.name)) {
                resistsTo.push(attackingType.name);
              }
              if (noEffect.includes(type.name)) {
                immuneTo.push(attackingType.name);
              }
            });

            // Calculate attacking (what this type is effective against)
            const superEffective = getSuperEffective(type.name);
            const notVeryEffective = getNotVeryEffective(type.name);
            const noEffect = getNoEffect(type.name);

            return (
              <div
                key={type.name}
                ref={(el) => (typeRefs.current[type.name] = el)}
                onClick={() => handleRowClick(type.name)}
                className={`bg-card rounded-lg border p-2 sm:p-4 transition-all duration-300 cursor-pointer hover:border-primary/50 ${
                  focusedType === type.name
                    ? "border-primary border-2 shadow-lg scale-[1.02]"
                    : sourceType === type.name
                      ? "border-secondary border-2 shadow-md"
                      : "border-border"
                }`}
              >
                <div className="grid grid-cols-[1fr_auto_auto_auto_1fr] gap-1 sm:gap-3 items-center">
                  {/* Left: What attacks this type (Defending) */}
                  <div className="flex flex-col gap-2 sm:gap-3">
                    {weakTo.length > 0 && (
                      <div>
                        <div className="text-[10px] sm:text-xs font-semibold text-muted-foreground mb-1 sm:mb-1.5 text-right">
                          WEAK TO (2×)
                        </div>
                        <div className="flex flex-wrap gap-1 sm:gap-2 justify-end">
                          {weakTo.map((attackingType) => (
                            <TypeBadge
                              key={attackingType}
                              typeName={attackingType}
                              highlight={
                                focusedType === type.name &&
                                sourceType === attackingType
                              }
                              onClick={() =>
                                handleTypeClick(attackingType, type.name)
                              }
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {resistsTo.length > 0 && (
                      <div>
                        <div className="text-[10px] sm:text-xs font-semibold text-muted-foreground mb-1 sm:mb-1.5 text-right">
                          RESISTS (0.5×)
                        </div>
                        <div className="flex flex-wrap gap-1 sm:gap-2 justify-end">
                          {resistsTo.map((attackingType) => (
                            <TypeBadge
                              key={attackingType}
                              typeName={attackingType}
                              variant="text"
                              highlight={
                                focusedType === type.name &&
                                sourceType === attackingType
                              }
                              onClick={() =>
                                handleTypeClick(attackingType, type.name)
                              }
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {immuneTo.length > 0 && (
                      <div>
                        <div className="text-[10px] sm:text-xs font-semibold text-muted-foreground mb-1 sm:mb-1.5 text-right">
                          IMMUNE TO (0×)
                        </div>
                        <div className="flex flex-wrap gap-1 sm:gap-2 justify-end">
                          {immuneTo.map((attackingType) => (
                            <TypeBadge
                              key={attackingType}
                              typeName={attackingType}
                              variant="text"
                              strikethrough
                              highlight={
                                focusedType === type.name &&
                                sourceType === attackingType
                              }
                              onClick={() =>
                                handleTypeClick(attackingType, type.name)
                              }
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Left Arrow */}
                  <div className="text-lg sm:text-2xl text-muted-foreground">
                    →
                  </div>

                  {/* Center: Type Badge */}
                  <div className="flex items-center justify-center">
                    <TypeBadge
                      typeName={type.name}
                      className="text-sm sm:text-base px-3 sm:px-5 py-2 sm:py-3 min-w-[80px] sm:min-w-[120px] font-bold"
                      onClick={() => handleCenterTypeClick(type.name)}
                    />
                  </div>

                  {/* Right Arrow */}
                  <div className="text-lg sm:text-2xl text-muted-foreground">
                    →
                  </div>

                  {/* Right: What this type attacks (Attacking) */}
                  <div className="flex flex-col gap-2 sm:gap-3">
                    {superEffective.length > 0 && (
                      <div>
                        <div className="text-[10px] sm:text-xs font-semibold text-muted-foreground mb-1 sm:mb-1.5">
                          SUPER EFFECTIVE (2×)
                        </div>
                        <div className="flex flex-wrap gap-1 sm:gap-2">
                          {superEffective.map((defendingType) => (
                            <TypeBadge
                              key={defendingType}
                              typeName={defendingType}
                              highlight={
                                focusedType === type.name &&
                                sourceType === defendingType
                              }
                              onClick={() =>
                                handleTypeClick(defendingType, type.name)
                              }
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {notVeryEffective.length > 0 && (
                      <div>
                        <div className="text-[10px] sm:text-xs font-semibold text-muted-foreground mb-1 sm:mb-1.5">
                          NOT VERY EFFECTIVE (0.5×)
                        </div>
                        <div className="flex flex-wrap gap-1 sm:gap-2">
                          {notVeryEffective.map((defendingType) => (
                            <TypeBadge
                              key={defendingType}
                              typeName={defendingType}
                              variant="text"
                              highlight={
                                focusedType === type.name &&
                                sourceType === defendingType
                              }
                              onClick={() =>
                                handleTypeClick(defendingType, type.name)
                              }
                            />
                          ))}
                        </div>
                      </div>
                    )}

                    {noEffect.length > 0 && (
                      <div>
                        <div className="text-[10px] sm:text-xs font-semibold text-muted-foreground mb-1 sm:mb-1.5">
                          NO EFFECT (0×)
                        </div>
                        <div className="flex flex-wrap gap-1 sm:gap-2">
                          {noEffect.map((defendingType) => (
                            <TypeBadge
                              key={defendingType}
                              typeName={defendingType}
                              variant="text"
                              strikethrough
                              highlight={
                                focusedType === type.name &&
                                sourceType === defendingType
                              }
                              onClick={() =>
                                handleTypeClick(defendingType, type.name)
                              }
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
